import { useState } from 'react';
import AgentAvatar from './AgentAvatar';
import InviteModal from './InviteModal';
import { formatExpiry } from '../utils/expiry';
import { apiFetch } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

export interface RoleSlot {
  role: string;
  status: 'BOUND' | 'PENDING' | 'EXPIRED' | 'UNBOUND';
  boundTo?: { userId: string; name: string; email: string } | null;
  invitationId?: string | null;
  expiresAt?: string | null;
}

interface Props {
  slot: RoleSlot;
  projectId: string;
  onInviteSuccess: () => void;
  /** How many roles slot.boundTo's user currently holds (BOUND) on this project. Only meaningful when slot.status === 'BOUND'. */
  userRoleCount?: number;
}

const ROLE_LABELS: Record<string, string> = {
  // API role values
  PM: 'Product Manager',
  ARCHITECT: 'Architect',
  TECH_LEAD: 'Tech Lead',
  DEVELOPER: 'Developer',
  QA: 'QA Engineer',
  DEVOPS: 'DevOps Engineer',
  STAKEHOLDER: 'Stakeholder',
  // Legacy / alternate keys
  PRODUCT_MANAGER: 'Product Manager',
  LEAD_ENGINEER: 'Lead Engineer',
  BACKEND_ENGINEER: 'Backend Engineer',
  FRONTEND_ENGINEER: 'Frontend Engineer',
  QA_ENGINEER: 'QA Engineer',
  DEVOPS_ENGINEER: 'DevOps Engineer',
  DESIGNER: 'Designer',
};

function StatusBadge({ status }: { status: RoleSlot['status'] }) {
  const styles: Record<RoleSlot['status'], React.CSSProperties> = {
    BOUND:   { background: 'rgba(16,185,129,0.12)',  color: 'var(--success)' },
    PENDING: { background: 'rgba(245,158,11,0.12)',  color: 'var(--warning)' },
    EXPIRED: { background: 'rgba(239,68,68,0.12)',   color: 'var(--error)' },
    UNBOUND: { background: 'rgba(120,113,108,0.12)', color: 'var(--text-secondary)' },
  };
  const labels: Record<RoleSlot['status'], string> = {
    BOUND: 'Filled', PENDING: 'Pending', EXPIRED: 'Expired', UNBOUND: 'Open',
  };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 9999,
        fontSize: 11,
        fontWeight: 600,
        fontFamily: 'var(--font-body)',
        ...styles[status],
      }}
    >
      {labels[status]}
    </span>
  );
}

export default function RoleSlotCard({ slot, projectId, onInviteSuccess, userRoleCount = 0 }: Props) {
  const { user: currentUser } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [actionError, setActionError] = useState('');
  const roleLabel = ROLE_LABELS[slot.role] ?? slot.role;
  const isSelf = !!currentUser && slot.boundTo?.userId === currentUser.id;
  const isLastRole = userRoleCount <= 1;

  const handleResend = async () => {
    if (!slot.invitationId) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiFetch(`/projects/${projectId}/invitations/${slot.invitationId}/resend`, { method: 'POST' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to resend';
      setActionError(msg.includes('404') || msg.includes('not found') ? 'Invitation no longer exists' : msg);
    } finally {
      setActionLoading(false);
      onInviteSuccess();
    }
  };

  const handleCancel = async () => {
    if (!slot.invitationId) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiFetch(`/projects/${projectId}/invitations/${slot.invitationId}`, { method: 'DELETE' });
      setConfirmCancel(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to cancel';
      setActionError(msg.includes('404') || msg.includes('not found') ? 'Invitation no longer exists' : msg);
      setConfirmCancel(false);
    } finally {
      setActionLoading(false);
      onInviteSuccess();
    }
  };

  const handleRemove = async () => {
    if (!slot.boundTo) return;
    setActionLoading(true);
    setActionError('');
    try {
      await apiFetch(`/projects/${projectId}/members/${slot.boundTo.userId}?role=${encodeURIComponent(slot.role)}`, { method: 'DELETE' });
      setConfirmRemove(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to remove member';
      setActionError(msg);
      setConfirmRemove(false);
    } finally {
      setActionLoading(false);
      onInviteSuccess();
    }
  };

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '12px 16px',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border)',
          background: 'var(--surface)',
        }}
      >
        {/* Left: avatar or placeholder */}
        <div style={{ flexShrink: 0 }}>
          {slot.status === 'BOUND' && slot.boundTo ? (
            <AgentAvatar agentType={slot.role.toLowerCase()} size={36} />
          ) : (
            <div
              style={{
                width: 36, height: 36,
                borderRadius: '50%',
                background: 'var(--surface-alt)',
                border: '1px dashed var(--border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, color: 'var(--text-secondary)',
              }}
            >
              👤
            </div>
          )}
        </div>

        {/* Middle: role + user info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, fontWeight: 600, color: 'var(--text)', margin: 0 }}>
              {roleLabel}
            </p>
            <StatusBadge status={slot.status} />
          </div>
          {slot.status === 'BOUND' && slot.boundTo && (
            <div>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {slot.boundTo.name} · {slot.boundTo.email}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: '4px 0 0' }}>
                {confirmRemove ? (
                  <>
                    <div>
                      <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, fontWeight: 600, color: 'var(--error)', margin: 0 }}>
                        {isLastRole
                          ? (isSelf ? 'Remove your access?' : `Remove ${slot.boundTo.name}'s access?`)
                          : `Remove ${roleLabel} role?`}
                      </p>
                      <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: '2px 0 0' }}>
                        {isLastRole
                          ? (isSelf
                              ? 'This is the only role you have on this project. Removing it will revoke access to this project entirely.'
                              : `This is the only role ${slot.boundTo.name} has on this project. Removing it will revoke access to this project entirely.`)
                          : (isSelf
                              ? `Are you sure you want to remove the ${roleLabel} role? You will still have access to this project through your other roles.`
                              : `Are you sure you want to remove the ${roleLabel} role? ${slot.boundTo.name} will still have access to this project through their other roles.`)}
                      </p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button
                        onClick={() => setConfirmRemove(false)}
                        style={{
                          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                          fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)',
                          textDecoration: 'underline',
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleRemove}
                        disabled={actionLoading}
                        style={{
                          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                          fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)',
                          textDecoration: 'underline', fontWeight: 600,
                        }}
                      >
                        {isLastRole ? 'Remove access' : 'Remove role'}
                      </button>
                    </div>
                  </>
                ) : (
                  <button
                    onClick={() => setConfirmRemove(true)}
                    style={{
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)',
                      textDecoration: 'underline', alignSelf: 'flex-start',
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
          )}
          {slot.status === 'PENDING' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '2px 0 0' }}>
              {slot.expiresAt && (
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--warning)' }}>
                  {formatExpiry(slot.expiresAt)}
                </span>
              )}
              <button
                onClick={handleResend}
                disabled={actionLoading}
                style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--primary)',
                  textDecoration: 'underline', opacity: actionLoading ? 0.5 : 1,
                }}
              >
                Resend
              </button>
              <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>·</span>
              {confirmCancel ? (
                <>
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)' }}>Cancel this invitation?</span>
                  <button
                    onClick={handleCancel}
                    disabled={actionLoading}
                    style={{
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)',
                      textDecoration: 'underline', fontWeight: 600,
                    }}
                  >
                    Confirm
                  </button>
                  <button
                    onClick={() => setConfirmCancel(false)}
                    style={{
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)',
                      textDecoration: 'underline',
                    }}
                  >
                    Dismiss
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setConfirmCancel(true)}
                  style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)',
                    textDecoration: 'underline',
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          )}
          {slot.status === 'EXPIRED' && slot.expiresAt && (
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', margin: '2px 0 0' }}>
              {formatExpiry(slot.expiresAt)}
            </p>
          )}
          {actionError && (
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--error)', margin: '4px 0 0' }}>
              {actionError}
            </p>
          )}
          {slot.status === 'UNBOUND' && (
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', margin: '2px 0 0' }}>
              No one assigned yet
            </p>
          )}
        </div>

        {/* Right: invite button */}
        {(slot.status === 'UNBOUND' || slot.status === 'EXPIRED') && (
          <button
            onClick={() => setShowModal(true)}
            style={{
              flexShrink: 0,
              fontFamily: 'var(--font-body)',
              fontSize: 13,
              fontWeight: 500,
              padding: '6px 14px',
              borderRadius: 'var(--radius-sm)',
              border: '1px solid rgba(124,58,237,0.3)',
              background: 'transparent',
              color: 'var(--primary)',
              cursor: 'pointer',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.06)'; e.currentTarget.style.borderColor = 'var(--primary)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(124,58,237,0.3)'; }}
          >
            {slot.status === 'EXPIRED' ? 'Re-invite' : 'Invite'}
          </button>
        )}
      </div>

      {showModal && (
        <InviteModal
          projectId={projectId}
          role={slot.role}
          onClose={() => setShowModal(false)}
          onSuccess={onInviteSuccess}
        />
      )}
    </>
  );
}
